import React, { useState, useEffect } from 'react';
import { Menu as MenuIcon, X } from 'lucide-react';
import Logo from './Logo';

/**
 * Horizontal top navigation bar.
 * Desktop: [nav items] .... [logo center] .... [hamburger right]
 * Mobile:  [logo left] .... [hamburger right]
 * Becomes sticky on scroll.
 */
const MenuHeader = ({ navItems = [] }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (target) => {
    setMenuOpen(false);
    const el =
      document.querySelector(`[data-section="${target}"]`) ||
      document.querySelector(`[data-subgroup="${target}"]`) ||
      document.getElementById(target);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-[#2B130F]/95 backdrop-blur-md border-b border-[#E8B945]/30 shadow-lg'
          : 'bg-[#2B130F]'
      }`}
      data-testid="menu-header"
      style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center">
        {/* Desktop: left half of nav items */}
        <nav className="hidden lg:flex items-center justify-end gap-5 xl:gap-7 flex-1">
          {navItems.slice(0, Math.ceil(navItems.length / 2)).map((item) => (
            <button
              key={item.target}
              onClick={() => scrollTo(item.target)}
              className="text-[#F5E6C9] uppercase tracking-wider text-[13px] xl:text-sm font-semibold hover:text-[#F5D06E] transition-colors whitespace-nowrap"
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
              data-testid={`nav-${item.target}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Mobile left-side logo */}
        <div className="lg:hidden">
          <Logo size="sm" />
        </div>

        {/* Centered logo (desktop) — between the two nav halves */}
        <div className="hidden lg:flex items-center justify-center px-6 xl:px-8 shrink-0">
          <Logo size="sm" />
        </div>

        {/* Desktop: right half of nav items */}
        <nav className="hidden lg:flex items-center justify-start gap-5 xl:gap-7 flex-1">
          {navItems.slice(Math.ceil(navItems.length / 2)).map((item) => (
            <button
              key={item.target}
              onClick={() => scrollTo(item.target)}
              className="text-[#F5E6C9] uppercase tracking-wider text-[13px] xl:text-sm font-semibold hover:text-[#F5D06E] transition-colors whitespace-nowrap"
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
              data-testid={`nav-${item.target}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="text-[#F5D06E] p-2 hover:scale-110 transition-transform ml-3 shrink-0"
          aria-label="Open menu"
          data-testid="hamburger-menu-button"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
        </button>
      </div>

      {/* Slide-down menu (all screens) */}
      {menuOpen && (
        <div
          className="absolute top-full left-0 right-0 z-50 bg-[#2B130F] border-y border-[#E8B945]/30 shadow-xl max-h-[70vh] overflow-y-auto"
          data-testid="slide-menu"
        >
          <nav className="flex flex-col py-2 max-w-7xl mx-auto">
            {navItems.map((item) => (
              <button
                key={item.target}
                onClick={() => scrollTo(item.target)}
                className="text-left px-6 py-3 text-[#F5E6C9] uppercase tracking-widest text-sm hover:bg-[#3A1E14] hover:text-[#F5D06E] transition-colors"
                data-testid={`menu-link-${item.target}`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="h-px bg-gradient-to-r from-transparent via-[#E8B945]/60 to-transparent" />
    </header>
  );
};

export default MenuHeader;
